import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useOrganization } from "../OrganizationContext"
import {
  auditAccountAsset,
  changeAccountAuditAssignment,
  completeGoogleAdsAuditOAuth,
  finalizeAccountAuditRun,
  loadAccountAuditRun,
  loadAccountAuditWorkspace,
  saveAccountAuditProfile,
  startAccountAuditRun,
  startGoogleAdsAuditOAuth,
} from "../api/accountAudits"
import { navigateCurrentWindow } from "../utils/browserNavigation"
import {
  Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle,
  Input, Table, TableBody, TableCell, TableContainer, TableHead,
  TableHeaderCell, TableRow,
} from "../components/ui"
import "./AccountAudits.css"

const PLATFORM_LABELS = {
  gtm: "Google Tag Manager", ga4: "Google Analytics 4",
  meta: "Meta Pixel", google_ads: "Google Ads",
}

const statusLabel = (status) => ({
  pass: "Passed", warning: "Warnings", fail: "Needs attention",
  unavailable: "Unavailable", complete: "Complete", partial: "Partial",
  failed: "Failed", running: "Running", queued: "Queued",
}[status] || status || "Not run")

const formatDate = (value) => {
  if (!value) return "—"
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleString()
}

const blankExpectation = () => ({
  canonical_name: "", required: true,
  platform_mappings: { gtm: [], ga4: [], meta: [], google_ads: [] },
})

const commaValues = (value) => String(value || "").split(",").map((item) => item.trim()).filter(Boolean)

async function runWithConcurrency(items, limit, worker, onProgress) {
  let cursor = 0
  let completed = 0
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor++
      await worker(items[index])
      completed += 1
      onProgress?.(completed, items.length)
    }
  })
  await Promise.all(runners)
}

export default function AccountAudits() {
  const { accountId: routeAccountId } = useParams()
  const navigate = useNavigate()
  const { organizations, selectedOrgId } = useOrganization()
  const organization = organizations.find((item) => item.id === selectedOrgId)
  const [workspace, setWorkspace] = useState(null)
  const [selectedAccountId, setSelectedAccountId] = useState(routeAccountId || "")
  const [dateRangeDays, setDateRangeDays] = useState(30)
  const [expectations, setExpectations] = useState([])
  const [results, setResults] = useState([])
  const [activeRun, setActiveRun] = useState(null)
  const [dependencyTab, setDependencyTab] = useState("triggers")
  const [selectedTriggerId, setSelectedTriggerId] = useState("")
  const [busy, setBusy] = useState("")
  const [progress, setProgress] = useState("")
  const [message, setMessage] = useState(null)
  const oauthHandled = useRef(false)

  const load = useCallback(async (preferredAccountId = selectedAccountId) => {
    if (!selectedOrgId) return
    setBusy((value) => value || "load")
    try {
      const data = await loadAccountAuditWorkspace(selectedOrgId, preferredAccountId)
      setWorkspace(data)
      const nextAccountId = data.accounts?.some((account) => account.id === preferredAccountId)
        ? preferredAccountId : data.accounts?.[0]?.id || ""
      setSelectedAccountId(nextAccountId)
      const profile = data.profiles?.find((item) => item.account_id === nextAccountId)
      setDateRangeDays(profile?.date_range_days || 30)
      setExpectations(data.expected_events?.filter((item) => item.account_id === nextAccountId) || [])
      const latestRun = data.runs?.find((run) => run.account_id === nextAccountId) || null
      setActiveRun(latestRun)
      setResults(latestRun ? data.latest_results || [] : [])
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    } finally {
      setBusy((value) => value === "load" ? "" : value)
    }
  }, [selectedAccountId, selectedOrgId])

  useEffect(() => { load(routeAccountId || selectedAccountId) }, [selectedOrgId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get("oauth_provider") !== "google_ads" || oauthHandled.current || !selectedOrgId) return
    const code = params.get("code")
    const state = params.get("state")
    const oauthError = params.get("error_description") || params.get("error")
    if (!code && !oauthError) return
    oauthHandled.current = true
    window.history.replaceState({}, document.title, window.location.pathname)
    if (oauthError) {
      setMessage({ type: "error", text: oauthError })
      return
    }
    setBusy("google-oauth")
    completeGoogleAdsAuditOAuth(selectedOrgId, code, state)
      .then((response) => {
        setMessage({ type: "success", text: `${response.customers?.length || 0} Google Ads test account(s) discovered.` })
        return load()
      })
      .catch((error) => setMessage({ type: "error", text: error.message }))
      .finally(() => setBusy(""))
  }, [load, selectedOrgId])

  const account = workspace?.accounts?.find((item) => item.id === selectedAccountId)
  const orgType = workspace?.organization?.organization_type || organization?.organizationType || "automotive"
  const runs = workspace?.runs?.filter((run) => run.account_id === selectedAccountId) || []
  const assignmentSets = useMemo(() => ({
    gtm: new Set((workspace?.assignments?.gtm || []).filter((row) => row.account_id === selectedAccountId).map((row) => row.gtm_container_connection_id)),
    meta: new Set((workspace?.assignments?.meta || []).filter((row) => row.account_id === selectedAccountId).map((row) => row.meta_pixel_connection_id)),
    google_ads: new Set((workspace?.assignments?.google_ads || []).filter((row) => row.account_id === selectedAccountId).map((row) => row.google_ads_customer_id)),
  }), [selectedAccountId, workspace])
  const ga4Assets = workspace?.accounts?.flatMap((owner) =>
    (owner.ga4_properties || []).map((property) => ({ ...property, owner_account_id: owner.id }))) || []
  const assignedCounts = {
    gtm: assignmentSets.gtm.size,
    ga4: account?.ga4_properties?.length || 0,
    meta: assignmentSets.meta.size,
    google_ads: assignmentSets.google_ads.size,
  }
  const gtmResult = results.find((result) => result.platform === "gtm" && result.dependencies)
  const dependencies = gtmResult?.dependencies || {}
  const selectedTrigger = (dependencies.triggers || []).find((trigger) => trigger.id === selectedTriggerId)

  const selectAccount = (id) => {
    setSelectedAccountId(id)
    navigate(`/account-audits/${id}`)
    load(id)
  }

  const toggleAsset = async (platform, assetId, assigned) => {
    setBusy(`asset-${platform}-${assetId}`)
    setMessage(null)
    try {
      await changeAccountAuditAssignment(selectedOrgId, selectedAccountId, platform, assetId, assigned)
      await load(selectedAccountId)
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    } finally {
      setBusy("")
    }
  }

  const saveProfile = async () => {
    setBusy("profile")
    setMessage(null)
    try {
      await saveAccountAuditProfile(selectedOrgId, selectedAccountId, {
        date_range_days: dateRangeDays,
        expected_events: expectations.filter((item) => item.canonical_name?.trim()),
      })
      setMessage({ type: "success", text: "Audit profile saved." })
      await load(selectedAccountId)
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    } finally {
      setBusy("")
    }
  }

  const updateExpectation = (index, field, value, platform) => setExpectations((current) =>
    current.map((event, eventIndex) => eventIndex !== index ? event : platform
      ? { ...event, platform_mappings: { ...event.platform_mappings, [platform]: commaValues(value) } }
      : { ...event, [field]: value }))

  const runAudit = async () => {
    setBusy("run")
    setMessage(null)
    setProgress("Preparing audit…")
    try {
      await saveAccountAuditProfile(selectedOrgId, selectedAccountId, {
        date_range_days: dateRangeDays,
        expected_events: expectations.filter((item) => item.canonical_name?.trim()),
      })
      const started = await startAccountAuditRun(selectedOrgId, selectedAccountId, dateRangeDays)
      setActiveRun(started.run)
      const nextResults = []
      const failedAssets = []
      await runWithConcurrency(started.assets || [], 3, async (asset) => {
        try {
          const response = await auditAccountAsset(selectedOrgId, started.run.id, asset)
          nextResults.push(response.result)
          setResults([...nextResults])
        } catch (error) {
          failedAssets.push({ asset, error })
        }
      }, (complete, total) => setProgress(`Audited ${complete} of ${total} assets…`))
      const finished = await finalizeAccountAuditRun(selectedOrgId, started.run.id)
      setActiveRun(finished.run)
      setResults(finished.results || [])
      setMessage({
        type: finished.run.status === "complete" ? "success" : "info",
        text: failedAssets.length
          ? `Audit ${finished.run.status}; ${failedAssets.length} asset request(s) can be retried.`
          : `Full-stack audit ${finished.run.status}.`,
      })
      await load(selectedAccountId)
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    } finally {
      setBusy("")
      setProgress("")
    }
  }

  const openRun = async (runId) => {
    setBusy("history")
    try {
      const response = await loadAccountAuditRun(selectedOrgId, runId)
      setActiveRun(response.run)
      setResults(response.results || [])
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    } finally { setBusy("") }
  }

  const retryAsset = async (result) => {
    if (!activeRun?.id) return
    const retryKey = `retry-${result.platform}-${result.asset_id}`
    setBusy(retryKey)
    setMessage(null)
    try {
      await auditAccountAsset(selectedOrgId, activeRun.id, result)
      const finished = await finalizeAccountAuditRun(selectedOrgId, activeRun.id)
      setActiveRun(finished.run)
      setResults(finished.results || [])
      setMessage({ type: "success", text: `${PLATFORM_LABELS[result.platform]} asset re-audited.` })
    } catch (error) {
      setMessage({ type: "error", text: error.message })
    } finally {
      setBusy("")
    }
  }

  const connectGoogleAds = async () => {
    setBusy("google-oauth")
    try {
      const response = await startGoogleAdsAuditOAuth(selectedOrgId)
      navigateCurrentWindow(response.authorization_url)
    } catch (error) {
      setMessage({ type: "error", text: error.message })
      setBusy("")
    }
  }

  if (!workspace && busy === "load") return <div className="page-container"><p>Loading Account Audits…</p></div>

  return (
    <div className="page-container page-container--wide account-audits ui-stack ui-stack--xl">
      <header className="ui-page-header account-audits__header">
        <div>
          <h1 className="ui-page-title">Account Audits</h1>
          <p className="ui-subtitle">Trace deployed measurement dependencies and compare platform health over time.</p>
        </div>
        <Badge variant="outline">{orgType === "standard" ? "Standard business" : "Automotive"}</Badge>
      </header>

      {message && <div className={`account-audits__message account-audits__message--${message.type}`} role={message.type === "error" ? "alert" : "status"}>{message.text}</div>}
      {workspace?.capabilities?.google_ads?.access_mode === "test" && (
        <div className="account-audits__message account-audits__message--info">Google Ads direct auditing is in test-account mode until the production developer token is approved.</div>
      )}

      <Card>
        <CardHeader><CardTitle>Audit account</CardTitle><CardDescription>Select an account, configure its assets, then run one saved audit.</CardDescription></CardHeader>
        <CardContent className="account-audits__toolbar">
          <label><span>Account</span><select value={selectedAccountId} onChange={(event) => selectAccount(event.target.value)}>{workspace?.accounts?.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          <label><span>Date range</span><select value={dateRangeDays} onChange={(event) => setDateRangeDays(Number(event.target.value))}><option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option></select></label>
          <Button onClick={runAudit} disabled={Boolean(busy) || !selectedAccountId}>{busy === "run" ? progress || "Running…" : "Run full audit"}</Button>
          <label><span>Audit history</span><select value={activeRun?.id || ""} onChange={(event) => openRun(event.target.value)} disabled={busy === "history"}><option value="">No run selected</option>{runs.map((run) => <option key={run.id} value={run.id}>{formatDate(run.created_at)} · {statusLabel(run.status)}</option>)}</select></label>
        </CardContent>
      </Card>

      <div className="account-audits__readiness">
        {Object.keys(PLATFORM_LABELS).map((platform) => (
          <Card key={platform}><CardContent><span className="account-audits__metric-label">{PLATFORM_LABELS[platform]}</span><strong>{assignedCounts[platform]}</strong><span>{assignedCounts[platform] === 1 ? "asset assigned" : "assets assigned"}</span></CardContent></Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle>Connected assets</CardTitle><CardDescription>Assign multiple assets to this account. Connections remain organization-owned and credentials stay server-side.</CardDescription></CardHeader>
        <CardContent className="account-audits__asset-groups">
          <AssetGroup title="GTM containers" platform="gtm" assets={workspace?.available_assets?.gtm || []} assigned={assignmentSets.gtm} label={(asset) => `${asset.container_name} (${asset.public_id || "no public ID"})`} busy={busy} onToggle={toggleAsset} empty="Connect a GTM container from Paid Media first." />
          <AssetGroup title="GA4 properties" platform="ga4" assets={ga4Assets} assigned={new Set((account?.ga4_properties || []).map((item) => item.id))} label={(asset) => `${asset.property_name || asset.property_id}${asset.owner_account_id !== selectedAccountId ? " · assigned elsewhere" : ""}`} busy={busy} onToggle={toggleAsset} empty="Add GA4 access before assigning a property." />
          <AssetGroup title="Meta Pixels" platform="meta" assets={workspace?.available_assets?.meta || []} assigned={assignmentSets.meta} label={(asset) => `${asset.pixel_name} (${asset.pixel_id})`} busy={busy} onToggle={toggleAsset} empty="Connect a Meta Pixel from Paid Media first." />
          <AssetGroup title="Google Ads customers" platform="google_ads" assets={workspace?.available_assets?.google_ads || []} assigned={assignmentSets.google_ads} label={(asset) => `${asset.customer_name || "Google Ads"} (${asset.customer_id})${asset.test_account ? " · Test" : ""}`} busy={busy} onToggle={toggleAsset} empty="No Google Ads customers discovered." extra={<Button size="sm" variant="outline" onClick={connectGoogleAds} disabled={Boolean(busy) || !workspace?.capabilities?.google_ads?.configured}>{busy === "google-oauth" ? "Connecting…" : "Connect Google Ads"}</Button>} />
        </CardContent>
      </Card>

      {orgType === "standard" ? (
        <Card>
          <CardHeader><CardTitle>Expected events and conversions</CardTitle><CardDescription>Map the same business event across GTM, GA4, Meta, and Google Ads. Separate multiple names with commas.</CardDescription></CardHeader>
          <CardContent className="ui-stack ui-stack--md">
            {expectations.map((event, index) => (
              <div className="account-audits__expectation" key={event.id || index}>
                <Input label="Business event" value={event.canonical_name || ""} onChange={(e) => updateExpectation(index, "canonical_name", e.target.value)} />
                {Object.keys(PLATFORM_LABELS).map((platform) => <Input key={platform} label={PLATFORM_LABELS[platform]} value={(event.platform_mappings?.[platform] || []).join(", ")} onChange={(e) => updateExpectation(index, null, e.target.value, platform)} />)}
                <label className="account-audits__required"><input type="checkbox" checked={event.required !== false} onChange={(e) => updateExpectation(index, "required", e.target.checked)} /> Required</label>
                <Button size="sm" variant="ghost" onClick={() => setExpectations((current) => current.filter((_, itemIndex) => itemIndex !== index))}>Remove</Button>
              </div>
            ))}
            <div className="account-audits__actions"><Button variant="outline" onClick={() => setExpectations((current) => [...current, blankExpectation()])}>Add expected event</Button><Button onClick={saveProfile} disabled={Boolean(busy)}>{busy === "profile" ? "Saving…" : "Save profile"}</Button></div>
          </CardContent>
        </Card>
      ) : (
        <Card><CardContent><strong>Automotive audit profile</strong><p className="ui-text--muted">Automotive accounts retain the existing documented event checklist and managed-tag installation workflow.</p></CardContent></Card>
      )}

      {activeRun && (
        <>
          <div className="account-audits__readiness">
            <AuditMetric label="Run status" value={statusLabel(activeRun.status)} />
            <AuditMetric label="Assets audited" value={`${activeRun.summary?.assets_audited || results.length} / ${activeRun.summary?.assets_expected || results.length}`} />
            <AuditMetric label="Findings" value={activeRun.summary?.findings ?? results.reduce((total, result) => total + (result.findings?.length || 0), 0)} />
            <AuditMetric label="Completed" value={formatDate(activeRun.completed_at)} />
          </div>
          <Card>
            <CardHeader><CardTitle>Platform results</CardTitle><CardDescription>Each asset is saved independently; unavailable providers do not discard successful results.</CardDescription></CardHeader>
            <CardContent><TableContainer><Table><TableHead><TableRow><TableHeaderCell>Platform</TableHeaderCell><TableHeaderCell>Asset</TableHeaderCell><TableHeaderCell>Status</TableHeaderCell><TableHeaderCell align="right">Findings</TableHeaderCell><TableHeaderCell>Changes</TableHeaderCell><TableHeaderCell>Action</TableHeaderCell></TableRow></TableHead><TableBody>{results.map((result) => <TableRow key={`${result.platform}-${result.asset_id}`}><TableCell>{PLATFORM_LABELS[result.platform]}</TableCell><TableCell>{result.asset_name || result.asset_id}</TableCell><TableCell><StatusBadge status={result.status} /></TableCell><TableCell align="right">{result.findings?.length || 0}</TableCell><TableCell>{result.changes?.has_changes ? `${changeCount(result.changes)} resource changes` : "No resource changes"}</TableCell><TableCell><Button size="sm" variant="outline" onClick={() => retryAsset(result)} disabled={Boolean(busy)}>{busy === `retry-${result.platform}-${result.asset_id}` ? "Retrying…" : "Retry asset"}</Button></TableCell></TableRow>)}</TableBody></Table></TableContainer></CardContent>
          </Card>
          <Findings results={results} />
          <ResourceChanges results={results} />
          {gtmResult && <DependencyExplorer dependencies={dependencies} tab={dependencyTab} setTab={setDependencyTab} selectedTrigger={selectedTrigger} selectTrigger={setSelectedTriggerId} />}
        </>
      )}
    </div>
  )
}

function AssetGroup({ title, platform, assets, assigned, label, busy, onToggle, empty, extra }) {
  return <section className="account-audits__asset-group"><div className="account-audits__asset-title"><h3>{title}</h3>{extra}</div>{assets.length ? assets.map((asset) => <label key={asset.id} className="account-audits__asset"><input type="checkbox" checked={assigned.has(asset.id)} disabled={Boolean(busy)} onChange={(event) => onToggle(platform, asset.id, event.target.checked)} /><span>{label(asset)}</span></label>) : <p className="ui-text--muted">{empty}</p>}</section>
}

function AuditMetric({ label, value }) {
  return <Card><CardContent><span className="account-audits__metric-label">{label}</span><strong className="account-audits__metric-value">{value}</strong></CardContent></Card>
}

function StatusBadge({ status }) {
  return <Badge variant="subtle" className={`account-audits__status account-audits__status--${status}`}>{statusLabel(status)}</Badge>
}

const changeCount = (changes) => Object.values(changes || {}).reduce((total, value) =>
  total + (value?.added?.length || 0) + (value?.removed?.length || 0) + (value?.changed?.length || 0), 0)

function ResourceChanges({ results }) {
  const rows = results.flatMap((result) => Object.entries(result.changes || {}).flatMap(([resourceType, changes]) => {
    if (!changes || typeof changes !== "object") return []
    return [
      ...(changes.added || []).map((id) => ({ platform: result.platform, asset: result.asset_name, resourceType, change: "Added", id })),
      ...(changes.removed || []).map((id) => ({ platform: result.platform, asset: result.asset_name, resourceType, change: "Removed", id })),
      ...(changes.changed || []).map((id) => ({ platform: result.platform, asset: result.asset_name, resourceType, change: "Changed", id })),
    ]
  }))
  return <Card><CardHeader><CardTitle>Changes since the previous audit</CardTitle><CardDescription>Added, removed, and changed normalized resources for each asset.</CardDescription></CardHeader><CardContent>{rows.length ? <TableContainer><Table><TableHead><TableRow><TableHeaderCell>Change</TableHeaderCell><TableHeaderCell>Platform</TableHeaderCell><TableHeaderCell>Resource</TableHeaderCell><TableHeaderCell>ID</TableHeaderCell><TableHeaderCell>Asset</TableHeaderCell></TableRow></TableHead><TableBody>{rows.map((row, index) => <TableRow key={`${row.platform}-${row.resourceType}-${row.id}-${index}`}><TableCell>{row.change}</TableCell><TableCell>{PLATFORM_LABELS[row.platform]}</TableCell><TableCell>{row.resourceType.replace("_", " ")}</TableCell><TableCell>{row.id}</TableCell><TableCell>{row.asset}</TableCell></TableRow>)}</TableBody></Table></TableContainer> : <p>No resource changes were detected.</p>}</CardContent></Card>
}

function Findings({ results }) {
  const findings = results.flatMap((result) => (result.findings || []).map((finding) => ({ ...finding, platform: result.platform, asset: result.asset_name })))
  return <Card><CardHeader><CardTitle>Prioritized findings</CardTitle><CardDescription>Configuration gaps and expected-event coverage across every audited asset.</CardDescription></CardHeader><CardContent>{!findings.length ? <p>No findings in this run.</p> : <TableContainer><Table><TableHead><TableRow><TableHeaderCell>Severity</TableHeaderCell><TableHeaderCell>Platform</TableHeaderCell><TableHeaderCell>Finding</TableHeaderCell><TableHeaderCell>Asset</TableHeaderCell></TableRow></TableHead><TableBody>{findings.sort((a, b) => (a.severity === "fail" ? -1 : 1) - (b.severity === "fail" ? -1 : 1)).map((finding, index) => <TableRow key={`${finding.code}-${finding.resource_id}-${index}`}><TableCell><StatusBadge status={finding.severity === "fail" ? "fail" : "warning"} /></TableCell><TableCell>{PLATFORM_LABELS[finding.platform]}</TableCell><TableCell>{finding.message}</TableCell><TableCell>{finding.asset}</TableCell></TableRow>)}</TableBody></Table></TableContainer>}</CardContent></Card>
}

function DependencyExplorer({ dependencies, tab, setTab, selectedTrigger, selectTrigger }) {
  const rows = dependencies[tab] || []
  return <Card><CardHeader><CardTitle>GTM dependency explorer</CardTitle><CardDescription>Published live container only. Select a trigger to see every firing and blocking consumer.</CardDescription></CardHeader><CardContent className="ui-stack ui-stack--md"><div className="account-audits__tabs">{["triggers", "tags", "variables", "platform_ids"].map((item) => <Button key={item} size="sm" variant={tab === item ? "secondary" : "ghost"} onClick={() => setTab(item)}>{item.replace("_", " ")}</Button>)}</div><TableContainer><Table><TableHead><TableRow><TableHeaderCell>Name / ID</TableHeaderCell><TableHeaderCell>Type</TableHeaderCell><TableHeaderCell>Usage</TableHeaderCell></TableRow></TableHead><TableBody>{rows.map((row) => <TableRow key={row.id}><TableCell>{tab === "triggers" ? <button className="account-audits__link-button" onClick={() => selectTrigger(row.id)}>{row.name}</button> : row.name || row.id}</TableCell><TableCell>{row.platform || row.type || "—"}</TableCell><TableCell>{tab === "tags" ? `${row.firing_trigger_ids?.length || 0} firing · ${row.blocking_trigger_ids?.length || 0} blocking` : tab === "triggers" ? `${row.firing_tags?.length || 0} firing · ${row.blocking_tags?.length || 0} blocking tags` : tab === "variables" ? `${row.consumers?.length || 0} consumers` : `${row.tags?.length || 0} tags`}</TableCell></TableRow>)}</TableBody></Table></TableContainer>{selectedTrigger && <div className="account-audits__trigger-detail"><h3>{selectedTrigger.name}</h3><p><strong>Fires:</strong> {selectedTrigger.firing_tags?.map((tag) => tag.name).join(", ") || "No tags"}</p><p><strong>Blocks:</strong> {selectedTrigger.blocking_tags?.map((tag) => tag.name).join(", ") || "No tags"}</p><p><strong>Variables:</strong> {selectedTrigger.variable_references?.join(", ") || "None"}</p></div>}</CardContent></Card>
}
